/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import styled from '@emotion/styled';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { BarDatum, BoxDatum, getNumDetectedTotal } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useExperimentalMetric } from '@resistapp/client/hooks/use-experimental-metric';
import { useNormalisationMode, useXAxisMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { SmallExternalLinkIcon } from '@resistapp/client/hooks/use-science-reference';
import { GeneGrouping, L2Targets } from '@resistapp/common/assays';
import { isAdmin } from '@resistapp/common/features';
import { friendlyAbundance, friendlyDate, friendlyL2Target, type StandardDateFormat } from '@resistapp/common/friendly';
import { friendlyAMRFamily, friendlyDrugClass, friendlyResistanceMechanism } from '@resistapp/common/friendly-amr';
import { getNormalisedValue, NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { isQaResultLabelDisqualifying, qaLabelDefinitions } from '@resistapp/common/qa-label-utils';
import {
  CardCategoryStat,
  CardIndex,
  CtsColumns,
  FullAbundance,
  NormalisationMode,
  QaLabelScope,
  XAxisMode,
} from '@resistapp/common/types';
import { SeriesPoint } from '@visx/shape/lib/types';
import { ScaleOrdinal } from 'd3-scale';
import { uniq, uniqBy, values } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { getGroupTextColor } from '../../shared/palettes';
import { getSampleSubtypeOrTypeLabel } from './tooltip-sampling-point-utils';

export type BarTooltipData = {
  bar: SeriesPoint<BarDatum>;
  key: string;
  index: number;
  height: number;
  width: number;
  y: number;
  color: string;
  label?: string;
};

export function getAbundace(datum: BarTooltipData) {
  const val = datum.bar.data[datum.key] as number | undefined;
  return +(val ?? 0);
}

export function isEstimateRangeMismatch(
  actualValue: number | null | undefined,
  estimateLow: number | null | undefined,
  estimateHigh: number | null | undefined,
) {
  return (
    actualValue != null &&
    estimateLow != null &&
    estimateHigh != null &&
    (actualValue < estimateLow || actualValue > estimateHigh)
  );
}

interface BarTooltipContentProps {
  tooltipData: BarTooltipData;
}

interface NumDetectedTooltipContentProps extends BarTooltipContentProps {
  tooltipData: BarTooltipData;
  colorScale: ScaleOrdinal<string, string>;
  grouping: GeneGrouping;
  showGeneInfo?: boolean;
}

function getQaLabelAuditPrefix(scope: QaLabelScope, techRep: number | null, sample: string): string {
  switch (scope) {
    case QaLabelScope.CHIP:
      return 'chip';
    case QaLabelScope.TECHREP:
      return techRep == null ? sample.split('-').slice(-1)[0] : `rep${techRep}`;
    case QaLabelScope.TRIPLICATE:
      return 'triplicate';
    case QaLabelScope.SAMPLE:
      return 'sample';
    case QaLabelScope.ASSAY:
      return 'assay';
    default:
      return 'label';
  }
}

export function NumDetectedTooltipContent({
  tooltipData,
  colorScale,
  grouping,
  showGeneInfo = false,
}: NumDetectedTooltipContentProps) {
  const { allGeneGroups } = useAssayContext();
  const { sampleById } = useResearchContext();
  const xAxisMode = useXAxisMode();
  const total = getNumDetectedTotal(tooltipData.bar.data);
  const groupColor = colorScale(tooltipData.key);
  const groupTextColor = getGroupTextColor(tooltipData.key, grouping, allGeneGroups);
  const sampleLabel =
    xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bar.data.bioNumber : tooltipData.bar.data.sampleNumber;
  const sampleSubtypeOrTypeLabel = getSampleSubtypeOrTypeLabel(sampleById.get(Number(tooltipData.bar.data.sampleId)));

  const abundance = getAbundace(tooltipData);

  return (
    <div style={{ padding: 8 }}>
      <div>
        <strong>Sample {sampleLabel}:</strong> {tooltipData.bar.data.label}
        {tooltipData.bar.data.date &&
          ` on ${friendlyDate(tooltipData.bar.data.date as StandardDateFormat, 'prefixMon', true)}`}
      </div>
      {sampleSubtypeOrTypeLabel ? (
        <div style={{ marginTop: oldTheme.spacing.min, fontSize: oldTheme.fontSize.s }}>
          <strong>Type:</strong> {sampleSubtypeOrTypeLabel}
        </div>
      ) : null}
      <div style={{ marginTop: oldTheme.spacing.xs, fontSize: oldTheme.fontSize.s }}>
        <strong>{total} genes detected in total</strong> in selected targets
      </div>
      {showGeneInfo && (
        <div style={{ marginTop: oldTheme.spacing.min, fontSize: oldTheme.fontSize.s }}>
          <strong>
            {abundance}{' '}
            <div
              style={{
                backgroundColor: groupColor,
                color: groupTextColor,
                borderRadius: '6px',
                padding: '2px 2px',
                display: 'inline-block',
              }}
            >
              {tooltipData.label ||
                (tooltipData.key in L2Targets ? friendlyL2Target(tooltipData.key as L2Targets) : tooltipData.key)}
            </div>{' '}
            genes detected
          </strong>
        </div>
      )}
    </div>
  );
}

interface HeatmapTooltipContentProps extends BarTooltipContentProps {
  grouping: GeneGrouping;
  colorScale: ScaleOrdinal<string, string>;
}

export function HeatmapTooltipContent({ colorScale, tooltipData, grouping }: HeatmapTooltipContentProps) {
  const { allGeneGroups, getGroup, allAssays } = useAssayContext();
  const normalisationMode = useNormalisationMode();
  const valueName = NormalisedValueName[normalisationMode];
  const groupName = getGroup(tooltipData.key, grouping) as string;
  const groupColor = colorScale(groupName);
  const groupTextColor = getGroupTextColor(groupName, grouping, allGeneGroups);
  const value = getAbundace(tooltipData);

  // Access research context to determine current abundance mode (e.g. Estimates)
  const { project, adminFeatures, queryFilters, sampleById, ctsBySampleId, ctsLoading } = useResearchContext();

  const sampleSubtypeOrTypeLabel = getSampleSubtypeOrTypeLabel(sampleById.get(Number(tooltipData.bar.data.sampleId)));

  // Flags for current abundance mode
  const abundanceMode = queryFilters?.filters?.abundances;
  const isEstimatesMode = abundanceMode === AbunanceSelection.ESTIMATES;
  const isMeasuredOrEstimateMode = abundanceMode === AbunanceSelection.MEASURED_OR_ESTIMATE;

  // Retrieve the original relative abundance from sample data
  const { abundanceByMode, measured, lowerBoundsByMode, upperBoundsByMode } = useMemo(() => {
    if (!project)
      return {
        abundanceByMode: null,
        measured: undefined,
        lowerBoundsByMode: null,
        upperBoundsByMode: null,
      };
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = sampleById.get(sampleId);
    const abundance = sample?.abundances.find(a => a.assay === assay);
    if (!abundance)
      return {
        abundanceByMode: null,
        measured: undefined,
        lowerBoundsByMode: null,
        upperBoundsByMode: null,
      };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const build = {} as Record<NormalisationMode, number | null>;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const lower = {} as Record<NormalisationMode, number | null>;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const upper = {} as Record<NormalisationMode, number | null>;
    values(NormalisationMode).forEach(mode => {
      build[mode] = getNormalisedValue(abundance, mode);
      lower[mode] = abundance.lowerBounds?.[mode] ?? null;
      upper[mode] = abundance.upperBounds?.[mode] ?? null;
    });
    return { abundanceByMode: build, measured: abundance.measured, lowerBoundsByMode: lower, upperBoundsByMode: upper };
  }, [project, tooltipData, getGroup, sampleById]);

  const lowerBound = lowerBoundsByMode ? lowerBoundsByMode[normalisationMode] : null;
  const upperBound = upperBoundsByMode ? upperBoundsByMode[normalisationMode] : null;

  const getEstimateForMode = (abundance: FullAbundance, mode: NormalisationMode): number | null =>
    abundance.estimates?.[mode] ?? null;

  let displayedValue: number | null | undefined = value;
  if (isEstimatesMode) {
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = project ? sampleById.get(sampleId) : undefined;
    const abundance = sample?.abundances.find(a => a.assay === assay);
    displayedValue = abundance ? getEstimateForMode(abundance, normalisationMode) : null;
  } else if (isMeasuredOrEstimateMode) {
    // Show N/A when gene is not measured
    displayedValue = measured ? value : null;
  }

  const { user } = useUser();
  const isAdminUser = isAdmin(user);
  const showAdmin = isAdminUser && adminFeatures.isModifyAsAdminActive;

  const adminInfo = useMemo(() => {
    if (!showAdmin || !project) return null;
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const assayInfo = allAssays.find(a => a.assay === assay);
    const ctsRepsAll: CtsColumns[] = ctsBySampleId?.[tooltipData.bar.data.sampleId]?.[assay] ?? [];

    // When showing CTs, pair AY1 by chip with the hovered assay.
    // If the hovered assay is AY1, nothing to filter; if non-AY1, filter AY1 replicates by the assay's chipId.
    const chipIdOfHoveredAssay = ctsRepsAll[0]?.chipId;
    const ctsReps: CtsColumns[] =
      assay === 'AY1' ? ctsRepsAll : ctsRepsAll.filter(r => r.assay !== 'AY1' || r.chipId === chipIdOfHoveredAssay);

    if (!ctsReps.length) return null;

    const sampleId = Number(tooltipData.bar.data.sampleId);
    const sample = sampleById.get(sampleId);
    const abundance = sample?.abundances.find(a => a.assay === assay);
    const stdCtFinal = abundance?.qaStdCt ?? null;

    const nbsp = '\u00A0';
    const spacer = `${nbsp}${nbsp}${nbsp}`; // three non-breaking spaces

    const buildStyledList = (field: 'ct' | 'efficiency' | 'tm') =>
      ctsReps.map((rep, idx) => {
        const style: React.CSSProperties = { fontFamily: 'monospace' };

        const isRejected =
          isQaResultLabelDisqualifying(rep.qaResultLabelType ?? null) ||
          (rep.qaResultLabelType == null && rep.qaRuleId != null);
        if (isRejected) style.textDecoration = 'line-through';

        let color: string | undefined;
        if (isQaResultLabelDisqualifying(rep.qaResultLabelType ?? null)) {
          color = 'red';
        } else if (rep.qaResultLabelType == null) {
          if (field === 'efficiency') {
            color = rep.qaRuleId === 4 ? 'orange' : rep.qaRuleId === 2 ? 'red' : undefined;
          } else if (field === 'ct') {
            color = rep.qaRuleId === 3 ? 'orange' : rep.qaRuleId === 1 ? 'red' : undefined;
          } else if (field === 'tm') {
            color = rep.qaRuleId === 0 ? 'red' : undefined;
          }
        }
        if (color) style.color = color;

        const val = field === 'ct' ? rep.ct : field === 'efficiency' ? rep.efficiency : rep.tm;
        const text = val == null ? '--.--' : val.toFixed(2);
        return (
          <span key={idx}>
            <code style={style}>
              {field === 'efficiency' && val != null ? '\u00A0' : ''}
              {text}
            </code>
            {idx < ctsReps.length - 1 ? spacer : ''}
          </span>
        );
      });

    const ctsStyled = buildStyledList('ct');
    const effStyled = buildStyledList('efficiency');
    const tmStyled = buildStyledList('tm');

    // Use non-breaking spaces (Unicode \u00A0) to preserve spacing in rendered HTML.
    // We also replace comma separators with three non-breaking spaces for a cleaner look.

    // Build status numbers so that they align vertically with the other technical-rep
    // columns.  Each numeric entry is rendered inside a monospace <code> element and
    // left-padded with non-breaking spaces to four characters (matching the width of
    // the 5-character numeric fields plus the three-space separator used below).
    const statusStyled = ctsReps.map((rep, idx) => {
      const displayValue = rep.qaResultLabelType != null ? 'M' : String(rep.qaRuleId ?? nbsp);
      const paddedRule = nbsp + nbsp + displayValue + nbsp + nbsp; // 4-char field
      return (
        <span key={idx}>
          <code style={{ fontFamily: 'monospace' }}>{paddedRule}</code>
          {idx < ctsReps.length - 1 ? spacer : ''}
        </span>
      );
    });

    const tmMin = assayInfo?.minMeltingTemperature;
    const tmMax = assayInfo?.maxMeltingTemperature;
    const tmRangeText = tmMin == null && tmMax == null ? '' : ` (r: ${tmMin ?? ''}-${tmMax ?? ''})`;

    const flags = Array.from(
      new Set(ctsReps.flatMap((ctRep: CtsColumns) => ctRep.flags?.split(/[,;\s]+/).filter(Boolean) || [])),
    );
    const first: CtsColumns | undefined = ctsReps[0];
    // Add a check for first being undefined, as ctsReps could be empty (for pooled projects, though unlikely due to earlier check?)
    if (!first) return null;
    const chip = project.chips.find(c => c.id === first.chipId);
    const chipInfo = chip ? `${chip.name} (${first.row},${first.column})` : `(${first.row},${first.column})`;

    const ruleDescriptionsStyled = ctsReps.map((rep, idx) => {
      const desc = rep.qaRuleDescription ? `${rep.qaRuleId}: ${rep.qaRuleDescription}` : '—';
      return (
        <span key={idx}>
          {desc}
          {idx < ctsReps.length - 1 ? spacer : ''}
        </span>
      );
    });

    const qaLabels = uniqBy(
      ctsReps.flatMap(rep => (rep.qaLabels ?? []).map(label => ({ label, repSample: rep.sample }))),
      ({ label }) => label.id,
    ).map(({ label, repSample }) => {
      const labelTitle = qaLabelDefinitions[label.labelType].title;
      const tmText = label.tm == null ? '' : ` @ ${label.tm.toFixed(1)}`;
      const resultText = label.appliesToResults ? (label.effectiveForResults ? 'results' : 'overridden') : 'training';
      const noteText = label.note.trim().length > 0 ? ` | ${label.note}` : '';
      return `${getQaLabelAuditPrefix(label.scope, label.techRep, repSample)}: ${labelTitle}${tmText} [${resultText}]${noteText}`;
    });

    return {
      ctsStyled,
      effStyled,
      tmStyled,
      statusStyled,
      ruleDescriptionsStyled,
      qaLabels,
      stdCt: stdCtFinal,
      flags,
      chipInfo,
      tmRangeText,
    };
  }, [showAdmin, tooltipData, getGroup, allAssays, project, sampleById, ctsBySampleId]);

  const estimateInfo = useMemo(() => {
    if (!showAdmin || !project) return null;
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = sampleById.get(sampleId);
    const abundance = sample?.abundances.find(a => a.assay === assay);
    if (!abundance) return null;
    const est = getEstimateForMode(abundance, normalisationMode);
    if (est == null) return null;
    const low = abundance.estimateLows?.[normalisationMode] ?? null;
    const high = abundance.estimateHighs?.[normalisationMode] ?? null;
    return {
      estimate: est,
      low,
      high,
    };
  }, [showAdmin, project, tooltipData, getGroup, normalisationMode, sampleById]);

  const { familyElements, mechanismElements } = useMemo(() => {
    const assayInfos = allAssays.filter(a =>
      grouping === 'assay' ? a.assay === groupName : getGroup(a.assay, grouping) === groupName,
    );

    const matches = assayInfos.flatMap(info => (info.card ? info.card.matches : []));
    const families = assayInfos.flatMap(info => (info.card ? info.card.amrFamilies : []));

    const familyStats = families.reduce<Record<string, CardCategoryStat>>((acc, f) => {
      if (Object.prototype.hasOwnProperty.call(acc, f.name)) {
        acc[f.name].coveredGenes += f.coveredGenes;
      } else {
        acc[f.name] = { ...f };
      }
      return acc;
    }, {});

    const nbsp = '\u00A0';

    const familyEntries = values(familyStats);

    const splitValues = (raw: string | null | undefined) =>
      raw
        ? raw
            .split(';')
            .map(v => v.trim())
            .filter(Boolean)
        : [];

    const mechanismStrings = uniq(matches.flatMap(m => splitValues(m.resistanceMechanism))).map(raw =>
      friendlyResistanceMechanism(raw).replace(/ /g, nbsp),
    );

    const mechanismElementsLocal = mechanismStrings.map((txt, idx) => (
      <span key={idx} style={{ whiteSpace: 'nowrap' }}>
        {txt}
        {idx < mechanismStrings.length - 1 && <br />}
      </span>
    ));

    const familyElementsLocal = familyEntries.map((f, idx) => {
      const nameText = friendlyAMRFamily(f.name).replace(/ /g, nbsp);
      return (
        <span key={idx} style={{ whiteSpace: 'nowrap' }}>
          {nameText}
          <span style={{ color: '#666' }}>
            {' '}
            ({f.coveredGenes}/{f.totalGenes})
          </span>
          {idx < familyEntries.length - 1 && <br />}
        </span>
      );
    });

    return { familyElements: familyElementsLocal, mechanismElements: mechanismElementsLocal };
  }, [allAssays, groupName, grouping, getGroup]);

  return (
    <div style={{ padding: 8 }}>
      <table style={{ width: '100%' }}>
        <tbody>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Group:</Label>
            <Value>
              <span
                style={{ backgroundColor: groupColor, color: groupTextColor, borderRadius: '6px', padding: '1px 4px' }}
              >
                {groupName in L2Targets ? friendlyL2Target(groupName as L2Targets) : groupName}
              </span>
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Sample:</Label>
            <Value>
              {tooltipData.bar.data.bioNumber} ({tooltipData.bar.data.sampleId})
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Name:</Label>
            <Value>{tooltipData.bar.data.label}</Value>
          </tr>
          {sampleSubtypeOrTypeLabel ? (
            <tr style={{ paddingBottom: '12px' }}>
              <Label>Type:</Label>
              <Value>{sampleSubtypeOrTypeLabel}</Value>
            </tr>
          ) : null}
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Gene:</Label>
            <Value>
              {tooltipData.key}
              {showAdmin && ` (${getGroup(tooltipData.key, 'assay') ?? tooltipData.key})`}
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>{valueName}:</Label>
            {(() => {
              let content: React.ReactNode;
              if (isMeasuredOrEstimateMode && measured === false) {
                content = 'N/A';
              } else if (displayedValue == null && (lowerBound != null || upperBound != null)) {
                if (lowerBound != null && upperBound != null) {
                  content = `(${friendlyAbundance(lowerBound, { significantN: 2 })}-${friendlyAbundance(upperBound, { significantN: 2 })})`;
                } else if (lowerBound != null) {
                  content = `≥ ${friendlyAbundance(lowerBound, { significantN: 2 })}`;
                } else {
                  content = `≤ ${friendlyAbundance(upperBound, { significantN: 2 })}`;
                }
              } else {
                content = friendlyAbundance(displayedValue);
              }
              return <Value>{content}</Value>;
            })()}
          </tr>
          {showAdmin &&
            estimateInfo &&
            (() => {
              const { low, high, estimate } = estimateInfo;
              const actual = abundanceByMode ? abundanceByMode[normalisationMode] : null;
              const isOutside = isEstimateRangeMismatch(actual, low, high);
              const rangeStyle: React.CSSProperties | undefined = isOutside
                ? { color: '#8B0000' } // dark red
                : undefined;
              return (
                <tr style={{ paddingBottom: '12px' }}>
                  <Label>{valueName} estim.:</Label>
                  <Value>
                    {friendlyAbundance(estimate, { significantN: 2, showLod: true })}{' '}
                    <span style={rangeStyle}>
                      ({friendlyAbundance(low, { significantN: 2 })}-{friendlyAbundance(high, { significantN: 2 })})
                    </span>
                  </Value>
                </tr>
              );
            })()}
          {showAdmin && (
            <>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>AMR gene&nbsp;families:</Label>
                <Value>
                  {!familyElements.length
                    ? 'N/A'
                    : familyElements.length > 10
                      ? `${familyElements.length} families`
                      : familyElements}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Resistance&nbsp;Mechanisms:</Label>
                <Value>{mechanismElements.length ? mechanismElements : 'N/A'}</Value>
              </tr>
            </>
          )}
          {showAdmin && ctsLoading && !adminInfo && (
            <tr style={{ paddingBottom: '12px' }}>
              <Label>Cq values:</Label>
              <Value>
                <i>Loading Cq rows…</i>
              </Value>
            </tr>
          )}
          {adminInfo && (
            <>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Chip:</Label>
                <Value>{adminInfo.chipInfo}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Cq values:</Label>
                <Value>
                  {adminInfo.ctsStyled}
                  {adminInfo.stdCt != null && (
                    <span style={{ fontStyle: 'italic' }}> (σ {adminInfo.stdCt.toFixed(2)})</span>
                  )}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Efficiencies:</Label>
                <Value>{adminInfo.effStyled}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Tm values:</Label>
                <Value>
                  {adminInfo.tmStyled}
                  {adminInfo.tmRangeText}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>QA rule:</Label>
                <Value>{adminInfo.statusStyled}</Value>
              </tr>
              {adminInfo.qaLabels.length > 0 && (
                <tr style={{ paddingBottom: '12px' }}>
                  <Label>QA labels:</Label>
                  <Value>{adminInfo.qaLabels.join(' | ')}</Value>
                </tr>
              )}
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Flags:</Label>
                <Value>{adminInfo.flags.join(', ') || '-'}</Value>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface GeneInfoTooltipContentProps {
  assay: string;
}

function CardGeneList({ matches }: { matches: CardIndex[] }) {
  // Show 3 genes initially, then expand in batches of 25 thereafter
  const FIRST_VISIBLE = 3;
  const BATCH_SIZE = 25;

  const [visibleCount, setVisibleCount] = useState(FIRST_VISIBLE);

  const uniqueMatches = uniqBy(matches, m => m.cardShortName ?? m.cvtermId);
  if (!uniqueMatches.length) return <>N/A</>;

  const handleExpand = () => {
    setVisibleCount(prev => {
      // If we're still showing the initial batch, jump to the first full batch (25 or total length)
      if (prev === FIRST_VISIBLE) {
        return Math.min(BATCH_SIZE, uniqueMatches.length);
      }
      // Otherwise add another batch of 25
      return Math.min(prev + BATCH_SIZE, uniqueMatches.length);
    });
  };

  const displayed = uniqueMatches.slice(0, visibleCount);

  const elements: React.ReactNode[] = [];

  displayed.forEach((m, idx) => {
    const accession = m.dnaAccession ?? m.proteinAccession;

    const geneElem = (
      <Fragment key={m.cvtermId ?? m.cardShortName ?? idx}>
        <a href={`https://card.mcmaster.ca/ontology/${m.cvtermId}`} target="_blank" rel="noopener noreferrer">
          {m.cardShortName}
        </a>
        {accession && (
          <a
            href={`https://www.ncbi.nlm.nih.gov/nuccore/${accession}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '2px', lineHeight: 1 }}
          >
            <SmallExternalLinkIcon
              icon={icon(faExternalLinkAlt)}
              style={{ verticalAlign: 'middle', position: 'relative', top: '-1px', marginRight: '2px' }}
            />
          </a>
        )}
      </Fragment>
    );

    elements.push(geneElem);

    const isLastDisplayed = idx === displayed.length - 1;
    if (!isLastDisplayed) {
      const shouldBreak = (idx + 1) % 5 === 0;
      elements.push(shouldBreak ? <br key={`br-${idx}`} /> : ' ');
    }
  });

  const hasMore = visibleCount < uniqueMatches.length;

  if (hasMore) {
    const isInitial = visibleCount === FIRST_VISIBLE;

    if (!isInitial) {
      // Put ellipsis on its own line for subsequent batches
      elements.push(<br key="br-final" />);
    } else {
      // Space before ellipsis to separate from the third gene
      elements.push(' ');
    }

    elements.push(
      <span key="ellipsis" style={{ cursor: 'pointer' }} onClick={handleExpand}>
        ...
      </span>,
    );
  }

  return <>{elements}</>;
}

export function GeneInfoTooltipContent({ assay }: GeneInfoTooltipContentProps) {
  const { allAssays } = useAssayContext();

  // Determine the relevant assays. If the provided key is an assay (starts with 'AY'),
  // we use it directly. Otherwise we treat the key as a gene name and collect all assays
  // whose gene field matches the provided key.
  const relevantAssays = assay.startsWith('AY')
    ? allAssays.filter(a => a.assay === assay)
    : allAssays.filter(a => a.gene === assay);

  const matches = relevantAssays.flatMap(info => info.card?.matches ?? []);
  const families = relevantAssays.flatMap(info => info.card?.amrFamilies ?? []);

  const familyStats = families.reduce<Record<string, CardCategoryStat>>((acc, f) => {
    if (Object.prototype.hasOwnProperty.call(acc, f.name)) {
      acc[f.name].coveredGenes += f.coveredGenes;
    } else {
      acc[f.name] = { ...f };
    }
    return acc;
  }, {});

  const nbsp = '\u00A0';

  const familyEntries = values(familyStats);

  const splitValues = (raw: string | null | undefined) =>
    raw
      ? raw
          .split(';')
          .map(v => v.trim())
          .filter(Boolean)
      : [];

  const mechanismStrings = uniq(matches.flatMap(m => splitValues(m.resistanceMechanism))).map(raw =>
    friendlyResistanceMechanism(raw).replace(/ /g, nbsp),
  );

  // Drug classes
  const drugClassStrings = uniq(matches.flatMap(m => splitValues(m.drugClass))).map(raw =>
    friendlyDrugClass(raw).replace(/ /g, nbsp),
  );

  // Dynamic label texts with correct singular/plural forms and optional counts
  const familyLabelText = `From ${familyEntries.length > 1 ? `${familyEntries.length} ` : ''}AMR gene\u00A0${
    familyEntries.length > 1 ? 'families' : 'family'
  }:`;
  const mechanismLabelText = `With ${mechanismStrings.length > 1 ? `${mechanismStrings.length} ` : ''}resistance\u00A0mechanism${
    mechanismStrings.length > 1 ? 's' : ''
  }:`;
  const drugClassLabelText = `Against ${drugClassStrings.length > 1 ? `${drugClassStrings.length} ` : ''}drug\u00A0class${
    drugClassStrings.length > 1 ? 'es' : ''
  }:`;

  // Build table rows for aligned layout
  const familyRows = familyEntries.length ? (
    familyEntries.map((f, idx) => {
      const nameText = friendlyAMRFamily(f.name).replace(/ /g, nbsp);
      return (
        <tr key={`fam-${idx}`} style={{ paddingBottom: '4px' }}>
          {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{familyLabelText}</Label> : <Label />}
          <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
            <a href={`https://card.mcmaster.ca/ontology/${f.cvtermId}`} target="_blank" rel="noopener noreferrer">
              {nameText}
            </a>
            <span style={{ color: '#666' }}>
              {' '}
              ({f.coveredGenes}/{f.totalGenes})
            </span>
          </Value>
        </tr>
      );
    })
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{familyLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  const mechanismRows = mechanismStrings.length ? (
    mechanismStrings.map((txt, idx) => (
      <tr key={`mech-${idx}`} style={{ paddingBottom: '4px' }}>
        {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{mechanismLabelText}</Label> : <Label />}
        <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>{txt}</Value>
      </tr>
    ))
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{mechanismLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  const drugClassRows = drugClassStrings.length ? (
    drugClassStrings.map((txt, idx) => (
      <tr key={`drug-${idx}`} style={{ paddingBottom: '4px' }}>
        {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{drugClassLabelText}</Label> : <Label />}
        <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>{txt}</Value>
      </tr>
    ))
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{drugClassLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  // Assay row (display all relevant assay IDs comma-separated)
  const assayStr = relevantAssays.map(a => a.assay).join(', ');
  const assayName = relevantAssays.map(a => a.gene).join(', ');
  const assayNameRow = (
    <tr style={{ paddingBottom: '4px' }}>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>Assay:</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
        {assayName} ({assayStr})
      </Value>
    </tr>
  );

  const uniqueMatches = uniqBy(matches, m => m.cardShortName ?? m.cvtermId);
  const cardLabelText = `Binds with ${uniqueMatches && uniqueMatches.length > 1 ? `${uniqueMatches.length} ` : ''}${uniqueMatches.length > 1 ? 'ARGs' : uniqueMatches.length === 1 ? 'ARG' : 'CARD gene'}:`;

  const cardRow = (
    <tr style={{ paddingBottom: '4px' }}>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{cardLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
        <CardGeneList matches={matches} />
      </Value>
    </tr>
  );

  return (
    <div style={{ fontSize: oldTheme.fontSize.s }}>
      <table style={{ width: '100%' }}>
        <tbody>
          {assayNameRow}
          {cardRow}
          {mechanismRows}
          {drugClassRows}
          {familyRows}
        </tbody>
      </table>
    </div>
  );
}

const Label = styled.td`
  ${oldTheme.fontWeight.bold}
  width: 230px;
  text-align: right;
  white-space: nowrap;
  vertical-align: top;
`;

const Value = styled.td`
  width: 230px;
  text-align: left;
  vertical-align: top;
`;

interface BoxTooltipContentProps {
  tooltipData: BoxDatum;
}

export function BoxTooltipContent({ tooltipData }: BoxTooltipContentProps) {
  const normalisationMode = useNormalisationMode();
  const xAxisMode = useXAxisMode();
  const { sampleById } = useResearchContext();
  const sampleLabel = xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bioNumber : tooltipData.sampleNumber;
  const sampleSubtypeOrTypeLabel = getSampleSubtypeOrTypeLabel(sampleById.get(Number(tooltipData.sampleId)));
  const showExperimentalMetrics = !!useExperimentalMetric();
  return (
    <>
      <div style={{ color: oldTheme.darkGray, padding: 8 }}>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Sample:</strong> {sampleLabel} ({tooltipData.sampleId})
        </div>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Name: </strong> {tooltipData.label}
        </div>
        {tooltipData.date ? (
          <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
            <strong>Date:</strong> {friendlyDate(tooltipData.date as StandardDateFormat, 'prefixMon', true)}
          </div>
        ) : null}
        {sampleSubtypeOrTypeLabel ? (
          <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
            <strong>Type:</strong> {sampleSubtypeOrTypeLabel}
          </div>
        ) : null}
      </div>
      <div style={{ fontSize: oldTheme.fontSize.s }}>
        {showExperimentalMetrics && (
          <>
            {Object.entries(tooltipData.experimentalMetrics || {}).map(([key, value]) => (
              <div key={key}>
                <b>{key}: </b>
                {(value as number | undefined)?.toFixed(key.startsWith('A') ? 1 : 0)}
              </div>
            ))}
            <br />
          </>
        )}
        <br />
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td />
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>
                {NormalisedValueNameShort[normalisationMode]}&nbsp;
              </td>
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>log₁₀</td>
            </tr>
            {tooltipData.max === tooltipData.min && (
              <tr>
                <td style={{ textAlign: 'right' }} />
                <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                <td>{tooltipData.max?.toFixed(6)}</td>
              </tr>
            )}
            {tooltipData.max !== tooltipData.min && (
              <>
                <tr>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10 }}>T</span>&nbsp;&nbsp;&nbsp;&nbsp; upper
                  </td>
                  <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                  <td>{tooltipData.max?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q3</td>
                  <td>{tooltipData.thirdQuartile ? friendlyAbundance(10 ** tooltipData.thirdQuartile) : '-'}</td>
                  <td>{tooltipData.thirdQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>×&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mean</td>
                  <td>{tooltipData.mean ? friendlyAbundance(10 ** tooltipData.mean) : '-'}</td>
                  <td>{tooltipData.mean?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>—&nbsp;median</td>
                  <td>{tooltipData.median ? friendlyAbundance(10 ** tooltipData.median) : '-'}</td>
                  <td>{tooltipData.median?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q1</td>
                  <td>{tooltipData.firstQuartile ? friendlyAbundance(10 ** tooltipData.firstQuartile) : '-'}</td>
                  <td>{tooltipData.firstQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>⊥&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;lower</td>
                  <td>{tooltipData.min ? friendlyAbundance(10 ** tooltipData.min) : '-'}</td>
                  <td>{tooltipData.min?.toFixed(6)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const NormalisedValueNameShort = {
  [NormalisationMode.SIXTEEN_S]: 'Rel. abund.',
  [NormalisationMode.MG_SS]: 'Copy num. per mg SS',
  [NormalisationMode.HOUR]: 'Copy num. per hour',
  [NormalisationMode.LITRE_16S]: 'Copy num. per L (16S std. curve)',
  [NormalisationMode.TEN_UL_DILUTED_DNA_16S]: 'Copies per 10 uL DNA (16S std. curve)',
  [NormalisationMode.LITRE_AY]: 'Copy num. per L (ay std. curve)',
  [NormalisationMode.TEN_UL_DILUTED_DNA_AY]: 'Copies per 10 uL DNA (ay std. curve)',
  [NormalisationMode.MG_BOD]: 'Copy num. per mg BOD',
} as const;
